import { Button } from "@/src/components/tailwind/Button";

import { AnimatedModal } from "@/src/components/AnimatedModal";

import {
  DataRoomFile,
  QuestionNodeType,
  useCreateQuestionMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useRef, useEffect, useState } from "react";

import { useDealQuery } from "@/src/graphql/generated";
import { H3 } from "@/src/components/Heading";
import { useQueryClient } from "@tanstack/react-query";
import { Option } from "@/src/components/tailwind/Dropdown";
import { CloseIcon } from "@/src/components/CloseIcon";
import { TextArea } from "@/src/components/tailwind/TextArea";
import { TextInput } from "@/src/components/tailwind/TextInput";
import { useParams } from "react-router-dom";

export function NewQuestionModal(props: {
  open: boolean;
  onClose: () => void;
  ddqlId?: string;
  parentDdqlId?: string;
  forDealFirmGroupId?: string;
}) {
  const { dealId } = useParams<{ dealId: string }>();
  const [files, setFiles] = useState<DataRoomFile[]>([]);
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState("");
  const [details, setDetails] = useState("");
  const [selectedDfg, setSelectedDfg] = useState<Option | null>(null);

  const [error, setError] = useState("");

  const createButtonRef = useRef<HTMLButtonElement>(null);

  const client = useGqlClient();
  const queryClient = useQueryClient();

  const dealQuery = useDealQuery(
    client,
    {
      id: dealId,
    },
    {
      enabled: dealId !== "",
    },
  );

  const createQuestion = useCreateQuestionMutation(client);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey) {
      if (event.key === "Enter") {
        event.preventDefault();
        if (createButtonRef.current) {
          createButtonRef.current.click();
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function clearForm() {
    setFiles([]);
    setTitle("");
    setDetails("");
    setTitleError("");
    setError("");
  }

  return (
    <AnimatedModal
      size="xxl"
      position="center"
      open={props.open}
      onClose={() => {
        clearForm();
        props.onClose();
      }}
    >
      <div>
        <div className="justify-between flex items-center">
          <H3>New question</H3>
          <CloseIcon onClose={props.onClose} />
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();

            if (!title) {
              setTitleError("Title is required");
              return;
            }

            let questionForDealFirmGroupId = props.forDealFirmGroupId;
            if (
              !questionForDealFirmGroupId &&
              selectedDfg &&
              selectedDfg.value
            ) {
              questionForDealFirmGroupId = selectedDfg.value;
            }

            createQuestion.mutate(
              {
                title,
                body: details,
                nodeType: QuestionNodeType.DataRoomFile,
                nodeId: files.length > 0 ? files[0].id : "",
                ddqlID: props.ddqlId,
                dealRoleOverrides: [],
              },
              {
                onSuccess: () => {
                  // Invalidate all questions
                  queryClient.invalidateQueries({
                    queryKey: ["Questions"],
                  });

                  // Invalidate the specific DDQL where the question was added
                  if (props.ddqlId) {
                    // Invalidate without deal ID (generic invalidation)
                    queryClient.invalidateQueries({
                      queryKey: ["Ddql", { id: props.ddqlId }],
                    });

                    // Invalidate with deal ID
                    if (dealQuery.data?.deal?.id) {
                      queryClient.invalidateQueries({
                        queryKey: [
                          "Ddql",
                          { id: props.ddqlId, dealId: dealQuery.data.deal.id },
                        ],
                      });
                    }
                  }

                  // Invalidate the parent DDQL if it exists
                  if (props.parentDdqlId) {
                    // Invalidate without deal ID (generic invalidation)
                    queryClient.invalidateQueries({
                      queryKey: ["Ddql", { id: props.parentDdqlId }],
                    });

                    // Invalidate with deal ID
                    if (dealQuery.data?.deal?.id) {
                      queryClient.invalidateQueries({
                        queryKey: [
                          "Ddql",
                          {
                            id: props.parentDdqlId,
                            dealId: dealQuery.data.deal.id,
                          },
                        ],
                      });
                    }
                  }

                  // Invalidate all DDQL queries to ensure proper refresh throughout the UI
                  queryClient.invalidateQueries({
                    queryKey: ["Ddql"],
                  });

                  if (props.forDealFirmGroupId) {
                    queryClient.invalidateQueries({
                      queryKey: [
                        "DealFirmGroupQuestions",
                        { id: props.forDealFirmGroupId },
                      ],
                    });
                  }

                  props.onClose();
                  clearForm();
                },
                onError: (e) => {
                  setError("Failed to create question");
                },
              },
            );
          }}
        >
          <div className="mt-4">
            <TextInput
              label="Title"
              placeholder="Title"
              value={title}
              error={titleError}
              onChange={(e) => {
                setTitle(e.currentTarget.value);
                setTitleError("");
              }}
            />

            <label
              htmlFor="details"
              className="block mt-3 text-sm font-medium leading-6 text-gray-900"
            >
              Details
            </label>
            <TextArea
              rows={3}
              name="details"
              value={details}
              onChange={(e) => {
                setDetails(e.currentTarget.value);
              }}
              placeholder="Any extra context/detail..."
            />

            <div className="flex justify-end mt-6 z-0">
              <Button
                ref={createButtonRef}
                isLoading={createQuestion.isPending}
                loadingText="Posting..."
                text="Post question"
                variant="positive"
                type="submit"
              />
            </div>
            {error ? <div className="mt-4  text-red-500">{error}</div> : null}
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
}
